import PropTypes from 'prop-types';
import useLiterals from 'utils/hooks/useLiterals';
import { formatCurrency } from 'utils/formatCurrency';
import TrustBadges from './TrustBadges';

const sumAmounts = (rows) => rows.reduce((acc, row) => acc + (Number(row.amount) || 0), 0);

const AmountRow = ({ label, amount, className = '' }) => (
  <div className={`flex items-center justify-between gap-4 text-sm ${className}`}>
    <span className='text-neutral-700'>{label}</span>
    <span className='text-neutral-800'>{formatCurrency(amount)}</span>
  </div>
);

AmountRow.propTypes = {
  label: PropTypes.string.isRequired,
  amount: PropTypes.number.isRequired,
  className: PropTypes.string,
};

/**
 * "Resumen de tu pago" sidebar card: one row per contract/section, optional
 * extras (with subtotal), the total and the trust badges.
 */
const PaymentSummaryCard = ({ summary = null, className = '' }) => {
  const { summary: literals } = useLiterals('paymentCheckout');
  const items = summary?.items || [];
  const extras = summary?.extras || [];
  const hasSummary = Boolean(summary);

  return (
    <section
      aria-labelledby='payment-summary-title'
      className={`rounded-card border border-neutral-100 bg-white p-6 shadow-card ${className}`}
    >
      <h2 id='payment-summary-title' className='m-0 mb-5 font-display text-xl font-semibold text-primary-700'>
        {literals.title}
      </h2>

      {!hasSummary ? (
        <p className='m-0 text-sm text-neutral-500'>{literals.empty}</p>
      ) : (
        <>
          <ul className='m-0 list-none divide-y divide-neutral-200 p-0'>
            {items.map((item) => (
              <li key={item.id} className='flex items-start justify-between gap-4 py-3 first:pt-0'>
                <span className='min-w-0'>
                  <span className='block text-sm font-semibold text-neutral-800'>{item.label}</span>
                  {item.description && (
                    <span className='block text-[13px] text-neutral-500'>{item.description}</span>
                  )}
                </span>
                <span className='shrink-0 text-sm text-neutral-800'>{formatCurrency(item.amount)}</span>
              </li>
            ))}
          </ul>

          {extras.length > 0 && (
            <div className='mt-3 space-y-3 border-t border-neutral-200 pt-3'>
              <AmountRow label={literals.subtotal} amount={sumAmounts(items)} />
              {extras.map((extra) => (
                <AmountRow key={extra.id} label={extra.label} amount={Number(extra.amount) || 0} />
              ))}
            </div>
          )}

          <div className='mt-4 flex items-center justify-between gap-4 border-t border-neutral-200 pt-4'>
            <span className='text-sm text-neutral-800'>{literals.total}</span>
            <span className='text-2xl font-bold text-primary-700'>{formatCurrency(summary.total)}</span>
          </div>
        </>
      )}

      <TrustBadges className='mt-6 border-t border-neutral-200 pt-6' />
    </section>
  );
};

export const summaryShape = PropTypes.shape({
  items: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    label: PropTypes.string.isRequired,
    description: PropTypes.string,
    amount: PropTypes.number.isRequired,
  })),
  extras: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    label: PropTypes.string.isRequired,
    amount: PropTypes.number.isRequired,
  })),
  total: PropTypes.number.isRequired,
});

PaymentSummaryCard.propTypes = {
  summary: summaryShape,
  className: PropTypes.string,
};

export default PaymentSummaryCard;
